// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
// Components
import { HighlightsContainer } from "./HighlightsContainer/HighlightsContainer";
import { CreateTechnologyHighlightsGroup } from "./CreateTechnologyHighlightsGroup/CreateTechnologyHighlightsGroup";
import { TitledTypedChild, DocumentHeaderSplitter, AsynchronousWrapper, SidebarFilterMenu } from "Components";
import { HighlightsGroupSidebarMenuItem } from "./HighlightsGroupSidebarMenuItem/HighlightsGroupSidebarMenuItem";
// Custom hooks
import { useFetch } from "Hooks";
// Stores
import { CaseContext, AuthContext, TechnologyContext } from "Stores";
// Types
import { TNotificationMessageDTO, TTechnologyCountDTO, TTechnologyHighlightsGroupDTO, TTechnologyHighlightsGroupsDTO, TUpdateTechnologyHighlightsGroupsDTO, TUseFetch } from "Types";
// Styles
import styles from "./technologyHighlights.module.scss";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Constants
import { FeatureToggleConstants, HubConstants, LoggingConstants, TechnologyHighlightsConstants } from "Constants";
// Enums
import { TechnologyActionTypeEnum, ToastTypeEnum } from "Enums";
// Interfaces
import { ISetTechnologyCountsAction } from "Interfaces";
// Contexts
import { UserContext, WebsocketContext } from "Providers";
// Helpers
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
import { GeneratedTechnologyHighlights } from ".";
import { FindestButton } from "Components/Shared";

export const TechnologyHighlights: FC = () => {
    // State
    const [coworkerTechnologyHighlightsGroups, setCoworkerTechnologyHighlightsGroups] =
        useState<TTechnologyHighlightsGroupDTO[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<number>(TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID);
    // Facilitate highlight generation
    const [doShowGeneratedHighlights, setDoShowGeneratedHighlights] = useState(false);
    const [generationDataType, setGenerationDataType] = useState("SCIENCE");

    // Contexts
    const { caseState } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { technologyDispatch } = useContext(TechnologyContext);
    const { webSocketController } = useContext(WebsocketContext);
    const { isCurrentUserFindest } = useContext(UserContext);

    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TTechnologyHighlightsGroupsDTO>
        = useFetch(`api/technologies/${caseState.selectedTechnologyId}/highlights`);
    const highlightsGroupsResourceName = caseState.selectedCaseId ? `${HubConstants.HIGHLIGHTS_GROUPS_RESOURCE_NAME_PREFIX}${caseState.selectedCaseId}` : "";

    // Logic
    const updateTechnologyHighlightsGroupsCount = useCallback(async (): Promise<void> => {
        if (!caseState.selectedTechnologyId) return;
        const newTechnologyCount: TTechnologyCountDTO = await TechnologyControllerSingleton.getTechnologyCountsAsync(caseState.selectedTechnologyId);
        technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: newTechnologyCount } as ISetTechnologyCountsAction);
    }, [caseState.selectedTechnologyId, technologyDispatch]);

    const updateCoworkerTechnologyHighlightsGroups = useCallback(async (updateTechnologyHighlightsGroups: TUpdateTechnologyHighlightsGroupsDTO): Promise<void> => {
        if (!caseState.selectedTechnologyId || !updateTechnologyHighlightsGroups || !updateTechnologyHighlightsGroups.newTechnologyHighlightsGroups) return;

        const newCoworkerTechnologyHighlightsGroups: TTechnologyHighlightsGroupDTO[] = [];

        if (updateTechnologyHighlightsGroups.newTechnologyHighlightsGroups.coworkerTechnologyHighlightsGroups &&
            updateTechnologyHighlightsGroups.newTechnologyHighlightsGroups.coworkerTechnologyHighlightsGroups.length >= 1) {
            newCoworkerTechnologyHighlightsGroups.push(...updateTechnologyHighlightsGroups.newTechnologyHighlightsGroups.coworkerTechnologyHighlightsGroups);
        } else {
            const allTechnologyHighlightsGroupDTO: TTechnologyHighlightsGroupDTO = {
                id: TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID,
                highlights: [],
                name: TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME
            };
            newCoworkerTechnologyHighlightsGroups.push(allTechnologyHighlightsGroupDTO);
        }

        setCoworkerTechnologyHighlightsGroups(newCoworkerTechnologyHighlightsGroups);

        if (updateTechnologyHighlightsGroups.updateTechnologyHighlightsCount) {
            await updateTechnologyHighlightsGroupsCount();
        }
    }, [caseState.selectedTechnologyId, updateTechnologyHighlightsGroupsCount]);

    useEffect(() => {
        if (!coworkerTechnologyHighlightsGroups ||
            (coworkerTechnologyHighlightsGroups && coworkerTechnologyHighlightsGroups.length < 1)) return;

        if (coworkerTechnologyHighlightsGroups.filter(group => group.id === selectedGroupId).length < 1) {
            setSelectedGroupId(coworkerTechnologyHighlightsGroups[0].id);
        }
    }, [coworkerTechnologyHighlightsGroups, selectedGroupId]);

    useEffect(() => {
        if (!fetchedData) return;
        updateCoworkerTechnologyHighlightsGroups({ updateTechnologyHighlightsCount: false, newTechnologyHighlightsGroups: fetchedData });
    }, [fetchedData, updateCoworkerTechnologyHighlightsGroups]);

    // Logging
    useEffect(() => {
        // Log the fetching of the highlights
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetHighlights`, {
            technologyId: caseState.selectedTechnologyId
        });
    }, [caseState.selectedTechnologyId]);

    const getCoworkerHighlightsContainers = (): JSX.Element | null => {
        if (!caseState.selectedCaseId || (!caseState.selectedOriginalTechnologyId && !caseState.selectedTechnologyId)) return null;

        let technologyId = -1;
        if (caseState.selectedOriginalTechnologyId) {
            technologyId = caseState.selectedOriginalTechnologyId;
        } else if (caseState.selectedTechnologyId) {
            technologyId = caseState.selectedTechnologyId;
        }
        if (technologyId === -1) return null;

        for (const group of coworkerTechnologyHighlightsGroups) {
            if (group.id === selectedGroupId) {
                return (
                    <HighlightsContainer
                        technologyHighlightsGroup={group}
                        allTechnologyHighlightsGroup={coworkerTechnologyHighlightsGroups}
                        isEditable={true}
                        technologyId={technologyId}
                        caseId={caseState.selectedCaseId}
                        refreshTechnologyHighlightsGroups={updateCoworkerTechnologyHighlightsGroups}
                        setNewSelectedGroupId={setSelectedGroupId}
                    />);
            }
        }

        return null;
    };

    const getCoworkerTechnologyHighlightsGroupSize = (coworkerTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO): number => {
        if (!coworkerTechnologyHighlightsGroup) return 0;

        if (coworkerTechnologyHighlightsGroup.id === TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID ||
            coworkerTechnologyHighlightsGroup.name.toLowerCase() === TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase()) {

            const highlightsAlreadyCount = new Set<number>();
            let groupSize = 0;

            for (const highlight of coworkerTechnologyHighlightsGroups.map(group => group.highlights).flat()) {
                if (!highlightsAlreadyCount.has(highlight.id)) {
                    groupSize++;
                    highlightsAlreadyCount.add(highlight.id);
                }
            }

            return groupSize;
        } else {
            return coworkerTechnologyHighlightsGroup.highlights.length;
        }
    };

    const handleHighlightsGroupsAlreadyBeingEdited = useCallback((): void => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing these groups.");
    }, []);

    const handleNotificationMessages = useCallback((notificationMessage: TNotificationMessageDTO): void => {
        ToastHelperSingleton.handleNotificationMessage(notificationMessage, authState.userEmail);
    }, [authState]);


    const handleTechnologyHighlightsGroupCreated = useCallback((newTechnologyHighlightsGroup: TTechnologyHighlightsGroupDTO): void => {
        // update component state
        setCoworkerTechnologyHighlightsGroups([...coworkerTechnologyHighlightsGroups, newTechnologyHighlightsGroup]);
    }, [coworkerTechnologyHighlightsGroups]);

    useEffect(() => {
        if (!highlightsGroupsResourceName) return;

        webSocketController.addHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${highlightsGroupsResourceName}`,
            handleHighlightsGroupsAlreadyBeingEdited);
        webSocketController.addHandler(`${HubConstants.UPDATE_PREFIX}${highlightsGroupsResourceName}`,
            updateCoworkerTechnologyHighlightsGroups);
        webSocketController.addHandler(`${HubConstants.CREATE_PREFIX}${highlightsGroupsResourceName}`,
            handleTechnologyHighlightsGroupCreated);
        webSocketController.addHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${highlightsGroupsResourceName}`,
            handleNotificationMessages);

        return () => {
            webSocketController.removeHandler(`${HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX}${highlightsGroupsResourceName}`,
                handleHighlightsGroupsAlreadyBeingEdited);
            webSocketController.removeHandler(`${HubConstants.UPDATE_PREFIX}${highlightsGroupsResourceName}`,
                updateCoworkerTechnologyHighlightsGroups);
            webSocketController.removeHandler(`${HubConstants.CREATE_PREFIX}${highlightsGroupsResourceName}`,
                handleTechnologyHighlightsGroupCreated);
            webSocketController.removeHandler(`${HubConstants.NOTIFICATION_MESSAGE_PREFIX}${highlightsGroupsResourceName}`,
                handleNotificationMessages);
        };
    }, [highlightsGroupsResourceName,
        handleHighlightsGroupsAlreadyBeingEdited,
        updateCoworkerTechnologyHighlightsGroups,
        handleTechnologyHighlightsGroupCreated,
        handleNotificationMessages,
        webSocketController]);

    const generateHighlights = (dataType: string) => {
        setDoShowGeneratedHighlights(true);
        setGenerationDataType(dataType);
    };

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            {FeatureToggleConstants.HighlightGeneration && isCurrentUserFindest ?
                <div>
                    <FindestButton title="Generate Science Highlights" onClick={() => { generateHighlights("SCIENCE");  }} />
                    <FindestButton title="Generate Patent Highlights" onClick={() => { generateHighlights("PATENTS"); }} />
                    {doShowGeneratedHighlights ?
                        <div className={styles.technologyHighlightsContainer}>
                            <DocumentHeaderSplitter title="Generated highlights" />
                            <GeneratedTechnologyHighlights dataType={generationDataType} />
                        </div>
                        : null}
                </div>
                : null}
            {fetchedData &&
                fetchedData.scoutTechnologyHighlightsGroups &&
                fetchedData.scoutTechnologyHighlightsGroups.length >= 1 ?
                <div className={styles.technologyHighlightsContainer}>
                    <DocumentHeaderSplitter title="Scout added highlights" />
                    <div style={{ display: "flex" }}>
                        <SidebarFilterMenu selectedFilterName={`highlights-group-${fetchedData.scoutTechnologyHighlightsGroups[0].id}`} extraClassName={styles.technologyHighlightsFilterMenu}>
                            {
                                fetchedData.scoutTechnologyHighlightsGroups
                                    .map((techHighGroup) => {
                                        return (
                                            <TitledTypedChild
                                                key={techHighGroup.id} title={`${techHighGroup.name} (${techHighGroup.highlights.length})`}
                                                typeName={`highlights-group-${techHighGroup.id}`}>
                                                {caseState.selectedCaseId &&
                                                    caseState.selectedTechnologyId ?
                                                    <HighlightsContainer
                                                        technologyHighlightsGroup={techHighGroup}
                                                        allTechnologyHighlightsGroup={[]}
                                                        isEditable={false}
                                                        technologyId={caseState.selectedTechnologyId}
                                                        caseId={caseState.selectedCaseId} />
                                                    :
                                                    <></>
                                                }
                                            </TitledTypedChild>
                                        );
                                    })
                            }
                        </SidebarFilterMenu>
                    </div>
                </div>
                :
                <></>
            }
            <div className={styles.technologyHighlightsContainer}>
                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ? <DocumentHeaderSplitter title="Coworker added highlights" /> : null}
                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery && coworkerTechnologyHighlightsGroups.length >= 1 ?
                    <div style={{ display: "flex", marginBottom: "1rem" }}>
                        <div className={styles.sidebarFilterMenu}>
                            {coworkerTechnologyHighlightsGroups.map((group, index) => {
                                return (
                                    <div key={group.id}>
                                        <HighlightsGroupSidebarMenuItem
                                            highlightsGroup={group}
                                            highlightsGroupSize={getCoworkerTechnologyHighlightsGroupSize(group)}
                                            itemIndexInSidebarMenu={index}
                                            selectedGroupId={selectedGroupId}
                                            setNewSelectedGroupId={setSelectedGroupId}
                                            coworkerTechnologyHighlightsGroups={coworkerTechnologyHighlightsGroups}
                                            highlightsGroupNameResourceName={`${HubConstants.HIGHLIGHTS_GROUP_RESOURCE_NAME_PREFIX}Name${group.id}`}
                                            highlightsGroupsResourceName={highlightsGroupsResourceName} />
                                    </div>
                                );
                            })
                            }
                            {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                <CreateTechnologyHighlightsGroup
                                    isInSidebar={true}
                                    setNewSelectedGroupId={setSelectedGroupId} />
                                :
                                null
                            }
                        </div>

                        <div style={{ width: "100%" }}>
                            {getCoworkerHighlightsContainers()}
                        </div>
                    </div>
                    :
                    <></>
                }
            </div>
        </AsynchronousWrapper>
    );
};
